import React, { useState } from "react";
import { MenuItemLink, getResources } from "react-admin";
import { useSelector } from "react-redux";
import {
	ExpandLess,
	ExpandMore,
	Storage as InventoryIcon,
	ShoppingCart as OrdersIcon,
	Settings as SettingsIcon,
	People as UsersIcon,
} from "@material-ui/icons";
import {
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Collapse,
	Divider,
	Icon,
} from "@material-ui/core";
import { SiteSwitcher } from "~/components/site-switcher.tsx";

// Need to create a custom icon component for the flag to avoid console errors in browser
function FlagIcon({ flag }: { flag: string }) {
	return (
		<Icon
			style={{
				fontSize: "1.5em",
				lineHeight: "1.2em",
				display: "inline-block",
				verticalAlign: "middle",
				filter: "brightness(1.5) contrast(1.5) saturate(1.5)",
				transform: "scale(1.2)",
			}}
		>
			{flag}
		</Icon>
	);
}

type Resource = {
	name: string;
	icon?: React.ComponentType<{ style?: React.CSSProperties }>;
	options?: {
		label?: string;
		permission?: string;
	};
};

type MenuGroup = {
	name: string;
	label: string;
	icon: React.ComponentType<{ style?: React.CSSProperties }>;
	resources: string[];
};

type CustomMenuProps = {
	onMenuClick?: () => void;
	hasDashboard?: boolean;
	regionFlag: string;
	siteCode: string;
	availableSites: readonly { code: string; url: string; label: string }[];
	backgroundColor: string;
};

// Define menu groups with their associated resources
const menuGroups: MenuGroup[] = [
	{
		name: "orders",
		label: "Orders",
		icon: OrdersIcon,
		resources: [
			"savedProjects",
			"cartProjects",
			"orderedProjects",
			"imageDetails",
		],
	},
	{
		name: "content",
		label: "Content",
		icon: OrdersIcon,
		resources: ["picToBrickCategories", "picsToBrick", "brickArtDesigns"],
	},
	{
		name: "inventory",
		label: "Inventory",
		icon: InventoryIcon,
		resources: [
			"manageInventory",
			"inventoryItems",
			"inventoryReports",
			"xeroErrors",
			"inventoryItemsLegacy",
			"inventoryReportsLegacy",
			"bulkInventoryCounts",
		],
	},
	{
		name: "pix-a-pic",
		label: "Pix-a-Pic",
		icon: OrdersIcon,
		resources: [
			"pixAPicCodeGenerations",
			"pixAPicCodes",
			"pixAPicTestimonials",
		],
	},
	{
		name: "settings",
		label: "Settings",
		icon: SettingsIcon,
		resources: [
			"instructionSettings",
			"pricingModel",
			"paletteColours",
			"inventorySettings",
			"inventorySkus",
			"inventoryWeights",
			"bulkInventoryItems",
		],
	},
];

export function CustomMenu(props: CustomMenuProps) {
	const {
		onMenuClick,
		hasDashboard,
		regionFlag,
		siteCode,
		availableSites,
		backgroundColor,
	} = props;
	const resources = useSelector(getResources);

	// State to track which submenus are open
	const [openSubmenus, setOpenSubmenus] = useState<{ [key: string]: boolean }>(
		{},
	);

	const handleSubmenuClick = (groupName: string) => {
		setOpenSubmenus((prev) => ({
			...prev,
			[groupName]: !prev[groupName],
		}));
	};

	// Get all resources that belong to any group
	const allGroupedResources = menuGroups.flatMap((group) => group.resources);

	// Filter resources into grouped and ungrouped
	const ungroupedResources = resources.filter(
		(resource: Resource) => !allGroupedResources.includes(resource.name),
	);

	return (
		<List
			component="nav"
			disablePadding
			style={{ width: "100%", padding: 0, color: "white", backgroundColor }}
		>
			<div style={{ padding: 16 }}>
				<SiteSwitcher
					currentSiteCode={siteCode}
					availableSites={availableSites}
				/>
			</div>
			{/* Dashboard Link */}
			{hasDashboard && (
				<MenuItemLink
					style={{ color: "white" }}
					to="/"
					primaryText={`Dashboard`}
					leftIcon={<FlagIcon flag={regionFlag} />}
					onClick={onMenuClick}
				/>
			)}

			{/* Users menu item at the top */}
			{hasDashboard &&
				ungroupedResources.find((r: Resource) => r.name === "users") && (
					<MenuItemLink
						style={{ color: "white" }}
						to="/users"
						primaryText="Users"
						leftIcon={<UsersIcon style={{ color: "white" }} />}
						onClick={onMenuClick}
					/>
				)}

			{/* Grouped menu items */}
			{menuGroups.map((group) => {
				// Get resources that actually exist for this group
				const groupResources = resources.filter((resource: Resource) =>
					group.resources.includes(resource.name),
				);

				// Don't render the group if no resources exist
				if (groupResources.length === 0) {
					return null;
				}

				const GroupIcon = group.icon;
				const isOpen = openSubmenus[group.name] || false;

				return (
					<div key={group.name}>
						<ListItem
							button
							onClick={() => handleSubmenuClick(group.name)}
							style={{
								paddingRight: 32,
								minHeight: 48,
								paddingLeft: 16,
								color: "white",
							}}
						>
							<ListItemIcon style={{ minWidth: 40, color: "white" }}>
								<GroupIcon style={{ color: "white" }} />
							</ListItemIcon>
							<ListItemText primary={group.label} style={{ marginRight: 8 }} />
							{isOpen ? <ExpandLess /> : <ExpandMore />}
						</ListItem>

						<Collapse in={isOpen} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
								{groupResources.map((resource: Resource) => {
									const ResourceIcon = resource.icon;
									return (
										<MenuItemLink
											key={resource.name}
											to={`/${resource.name}`}
											primaryText={resource.options?.label || resource.name}
											leftIcon={
												ResourceIcon ? (
													<ResourceIcon style={{ color: "white" }} />
												) : undefined
											}
											onClick={onMenuClick}
											style={{
												paddingLeft: 32,
												paddingRight: 16,
												fontSize: "0.875rem",
												color: "white",
											}}
										/>
									);
								})}
							</List>
						</Collapse>
					</div>
				);
			})}

			{ungroupedResources.length > 0 && <Divider />}

			{/* Ungrouped menu items */}
			{ungroupedResources.map((resource: Resource) => {
				// Skip users here as it will be rendered at the top
				if (resource.name === "users") return null;
				const ResourceIcon = resource.icon;
				return (
					<MenuItemLink
						style={{ color: "white", paddingRight: 16 }}
						key={resource.name}
						to={`/${resource.name}`}
						primaryText={resource.options?.label || resource.name}
						leftIcon={
							ResourceIcon ? (
								<ResourceIcon style={{ color: "white" }} />
							) : undefined
						}
						onClick={onMenuClick}
					/>
				);
			})}
		</List>
	);
}
